import { SELECTOR_DESCRIPTORS_BY_PRIORITY, SELECTOR_REGISTRY } from '../selectors/registry';
import type { SelectorType } from '../selectors/registry';
import type { CtaLinkComponent, ParsedComponent } from '../types/chat';

/**
 * Parse a component payload into a validated `ParsedComponent`.
 * Accepts either a JSON string or an already-parsed object (as returned
 * inline from session content records).
 *
 * When the payload is wrapped in `{ type, data }`, the outer `type` is the
 * source of truth: we look up the matching descriptor directly and validate
 * against its `data`. Falling through to shape-only matching would silently
 * misroute payloads whose data shape overlaps multiple selectors (e.g. a
 * review-method payload whose options happen to validate as icon-tiles).
 *
 * If the payload is bare (no `type` wrapper) we fall back to trying every
 * descriptor in priority order.
 */
export function parseComponentPayload(
    input: string | Record<string, unknown>
): ParsedComponent | CtaLinkComponent | null {
    try {
        const raw = typeof input === 'string' ? JSON.parse(input) : input;
        const payload =
            typeof raw === 'object' &&
            raw !== null &&
            'type' in raw &&
            'data' in raw &&
            typeof raw.data === 'object' &&
            raw.data !== null
                ? raw.data
                : raw;

        // cta-link is not a selector — handle it before the registry lookup
        if (typeof raw === 'object' && raw !== null && 'type' in raw && raw.type === 'cta-link') {
            const data =
                'data' in raw && typeof raw.data === 'object' && raw.data !== null
                    ? (raw.data as Record<string, unknown>)
                    : {};
            return {
                type: 'cta-link',
                urlKey: typeof data.url_key === 'string' ? data.url_key : '',
                label: typeof data.label === 'string' ? data.label : undefined,
            };
        }

        // When the payload has an explicit type, look up the descriptor directly
        // instead of relying on priority iteration (which can match the wrong
        // descriptor when validators are permissive).
        if (
            typeof raw === 'object' &&
            raw !== null &&
            'type' in raw &&
            typeof raw.type === 'string' &&
            raw.type in SELECTOR_REGISTRY
        ) {
            const descriptor = SELECTOR_REGISTRY[raw.type as SelectorType];
            return descriptor.validate(payload === raw ? {} : payload);
        }

        for (const descriptor of SELECTOR_DESCRIPTORS_BY_PRIORITY) {
            const validated = descriptor.validate(payload);
            if (validated) return validated;
        }
    } catch {
        // Intentionally silent: malformed JSON or invalid payloads are expected
        // during normal operation (e.g., user-typed text, legacy formats).
        // Returning null signals "not a valid component" to caller.
    }
    return null;
}
